import React, { useState, useEffect } from "react";
import {
  Container,
  Grid,
  Box,
  Typography,
  Paper,
  Radio,
  RadioGroup,
  Backdrop,
  CircularProgress,
  FormControlLabel,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import NoteIcon from "@mui/icons-material/Note";
import { Doughnut } from "react-chartjs-2";
import { LineChart } from "@mui/x-charts/LineChart";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import axios from "axios";
import { BASE_API_URL } from "../../constants";
import { xorDecrypt, xorEncrypt } from "../../encrypt";
import axiosInstance from "../../api/axiosInstance";
import LineGraph from "../../Components/LineGraph";
ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const centerTextPlugin = {
  id: "centerText",
  beforeDraw: function (chart) {
    const { width, height, ctx } = chart;
    ctx.restore();
    const fontSize = (height / 140).toFixed(2);
    ctx.font = `${fontSize}em sans-serif`;
    ctx.textBaseline = "middle";

    const text1 = "Credits";
    const text2 = chart.config.options.plugins.centerText.text;

    const text1X = Math.round((width - ctx.measureText(text1).width) / 2);
    const text2X = Math.round((width - ctx.measureText(text2).width) / 2);
    const text1Y = height / 2 - 10;
    const text2Y = height / 2 + 10;

    ctx.fillStyle = "white";
    ctx.fillText(text1, text1X, text1Y);
    ctx.fillStyle = "#ffa500";
    ctx.fillText(text2, text2X, text2Y);
    ctx.save();
  },
};

ChartJS.register(centerTextPlugin);

const SubscriberDashboard = () => {
  const [loading, setLoading] = useState(true); // Add loading state
  const [creditsUsed, setCreditsUsed] = useState(0);
  const [totalCredits, setTotalCredits] = useState(100);
  const [targetCreditsUsed, setTargetCreditUsed] = useState(0);
  const creditsLeft = totalCredits - creditsUsed;
  const creditsPercentage = (creditsUsed / totalCredits) * 100;
  const cutoutPercentage = `${creditsPercentage}%`;
  const [totalSessionNotes, setTotalSessionNotes] = useState(0);
  const [totalClients, setTotalClients] = useState(0);
  const [sessionNotes, setSessionNotes] = useState([]);
  const [selectedGraph, setSelectedGraph] = useState("sessionNotes");
  const [creditLogs, setCreditLogs] = useState([]);

  const pieData = {
    labels: ["Credits Used", "Credits Left"],
    datasets: [
      {
        label: "Credits",
        data: [creditsUsed, creditsLeft],
        backgroundColor: ["#ffa500", "rgba(255, 255, 255, 0.1)"],
        borderWidth: 0,
        hoverOffset: 4,
        borderRadius: 50, // Set the border radius to have rounded corners
      },
    ],
  };

  const pieOptions = {
    plugins: {
      tooltip: {
        enabled: false,
      },
      legend: {
        display: false,
      },
      centerText: {
        display: true,
        text: `${creditsUsed}/${totalCredits}`,
      },
    },
    cutout: cutoutPercentage, // Adjust cutout dynamically
  };

  const boxStyle = {
    padding: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    border: "2px solid #f5a623",
    borderRadius: 4,
    marginTop: 2,
    backgroundColor: "rgba(255, 255, 255, 0)", // Adjust the transparency as needed
  };

  const iconStyle = {
    marginRight: 10,
    color: "#f5a623",
  };
  const colorfulTextStyle = {
    background: "linear-gradient(45deg, #ffa500, #ffffff, #ffa500)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    fontWeight: "bold",
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const encryptedUsername = localStorage.getItem("username");
        if (!encryptedUsername) {
          throw new Error("Username not found in localStorage");
        }

        const email = xorDecrypt(encryptedUsername);
        const response = await axiosInstance.post(
          `${BASE_API_URL}/liveinfo`,
          { username: email },
          { withCredentials: true }
        );

        if (!response.data.userList || response.data.userList.length === 0) {
          throw new Error("User list is empty or undefined");
        }

        const user = response.data.userList[0];
        //console.log("User data", user);

        let activeEnrollment;
        for (let i = user.enrollments.length - 1; i >= 0; i--) {
          if (user.enrollments[i].enrollmentStatus === "Active") {
            activeEnrollment = user.enrollments[i];
            break;
          }
        }

        const enrollmentStatus = activeEnrollment ? "true" : "false";
        //console.log("Enrollment Status:", enrollmentStatus);

        if (enrollmentStatus === "true") {
          //console.log("Active enrollment found:", activeEnrollment);
          localStorage.setItem(
            "productPlan",
            xorEncrypt(activeEnrollment.productPlan)
          );
          setTargetCreditUsed(activeEnrollment.apiCallsLeft);

          // Set the latest enrollment ID in local storage
          const latestEnrollmentId = activeEnrollment.id;
          localStorage.setItem(
            "latestEnrollmentId",
            xorEncrypt(latestEnrollmentId.toString())
          );

          // Encrypt and store enrollment status
          const encryptedEnrollmentStatus = xorEncrypt(enrollmentStatus);
          localStorage.setItem("enrollmentStatus", encryptedEnrollmentStatus);
        } else {
          const latestEnrollment =
            user.enrollments[user.enrollments.length - 1];
          if (latestEnrollment && latestEnrollment.enrollmentEndDate) {
            const enrollmentEndDate = new Date(
              latestEnrollment.enrollmentEndDate
            );
            const now = new Date();
            const differenceInDays =
              (now - enrollmentEndDate) / (1000 * 60 * 60 * 24);

            if (differenceInDays <= 7) {
              localStorage.setItem(
                "enrollmentEndDate",
                enrollmentEndDate.toISOString()
              );
            }
          }
        }

        const notesByDays = response.data.notesByDays || {};
        const transformedData = Object.keys(notesByDays)
          .sort((b, a) => new Date(b) - new Date(a))
          .map((date) => {
            const breakdown = notesByDays[date];
            const totalNotes = Object.values(breakdown).reduce(
              (sum, count) => sum + count,
              0
            );
            return { date, value: totalNotes, breakdown };
          });

        setSessionNotes(transformedData);

        const apiCallLogs = response.data.apiCallLogs || {};
        const transformedCredits = Object.keys(apiCallLogs).map((date) => ({
          date,
          value: apiCallLogs[date],
        }));
        setCreditLogs(transformedCredits);

        setTotalCredits(response.data.totalCreds); // assuming total credits from clients
        setTotalClients(response.data.totalClients);
        setTotalSessionNotes(response.data.totalNotes);
        setLoading(false); // Set loading to false after data is fetched
      } catch (error) {
        console.error("Error fetching data", error);
        setLoading(false); // Set loading to false in case of error
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    let currentCredits = 0;
    const interval = setInterval(() => {
      if (currentCredits < targetCreditsUsed) {
        currentCredits += 1;
        setCreditsUsed(currentCredits);
      } else {
        clearInterval(interval);
      }
    }, 20); // Adjust the interval for animation speed
    return () => clearInterval(interval);
  }, [targetCreditsUsed]);

  const handleGraphChange = (event) => {
    setSelectedGraph(event.target.value);
  };

  return (
    <>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backdropFilter: "blur(5px)",
        }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Container padding={5}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={6}>
            <Paper sx={boxStyle}>
              <Box display="flex" alignItems="center">
                <NoteIcon sx={iconStyle} />
                <Typography variant="h6" sx={{ color: "white" }}>
                  Total Session Notes
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: "white" }}>
                {totalSessionNotes}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Paper sx={boxStyle}>
              <Box display="flex" alignItems="center">
                <PeopleIcon sx={iconStyle} />
                <Typography variant="h6" sx={{ color: "white" }}>
                  Total Clients
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: "white" }}>
                {totalClients}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={12}>
            <Paper
              sx={{
                padding: 2,
                position: "relative",
                border: "2px solid #f5a623",
                borderRadius: 4,
                background: "rgba(255, 255, 255,0.1 )",
              }}
            >
              <Typography variant="h4" sx={colorfulTextStyle}>
                DATA ANALYSIS
              </Typography>
              <RadioGroup
                row
                value={selectedGraph}
                onChange={handleGraphChange}
                sx={{ marginBottom: 2, color: "white" }}
              >
                <FormControlLabel
                  value="sessionNotes"
                  control={<Radio />}
                  label="Session Notes"
                />
                <FormControlLabel
                  value="credits"
                  control={<Radio />}
                  label="Credits"
                />
              </RadioGroup>

              {/* Display Graphs Based on Selection */}
              {selectedGraph === "sessionNotes" && sessionNotes.length > 0 && (
                <LineGraph
                  sessionNotes={sessionNotes}
                  type="sessionNotes"
                  sx={{
                    height: 500,
                    marginTop: 3,
                  }}
                />
              )}
              {selectedGraph === "credits" && creditLogs.length > 0 && (
                <LineGraph
                  sessionNotes={creditLogs} // Reusing the LineGraph component
                  type="credits"
                  sx={{
                    height: 500,
                    marginTop: 3,
                  }}
                />
              )}
              {selectedGraph === "credits" && creditLogs.length === 0 && (
                <Typography
                  variant="body1"
                  align="center"
                  sx={{ color: "white", marginTop: 14, marginBottom: 15 }}
                >
                  No data available for credits.
                </Typography>
              )}
              {selectedGraph === "sessionNotes" &&
                sessionNotes.length === 0 && (
                  <Typography
                    variant="body1"
                    align="center"
                    sx={{ color: "white", marginTop: 14, marginBottom: 15 }}
                  >
                    No data available for session notes.
                  </Typography>
                )}

              <Box
                sx={{
                  position: "absolute",
                  top: "10px",
                  right: "10px",
                  width: 110,
                  height: 110,
                  borderRadius: "50%",
                  display: "flex",
                  margin: "6px",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "20px",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    margin: "0 auto",
                    backdropFilter: "blur(15px)",
                    borderRadius: "100px",
                  }}
                >
                  <Doughnut data={pieData} options={pieOptions} />
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default SubscriberDashboard;
